import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type StageChecklistResult } from '@whinerp/client';
import { Icon, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * What this deal still needs before it can move on (F-043).
 *
 * The inline checklist the feature asks for, and it reads the same criteria
 * the move refuses with. Two implementations - one to check and one to display
 * - is the arrangement where a checklist says a deal is ready and the button
 * says it is not, and the rep believes the checklist.
 *
 * Drawn only when the stage asks for something. A panel reading "nothing
 * required" on every deal in a workspace that has not configured criteria is a
 * panel people learn to skip, and then miss on the one pipeline that uses it.
 */
export function StageChecklistPanel({ dealId }: { dealId: string }): JSX.Element | null {
  const t = useT();
  const checklist = useQuery<StageChecklistResult, ApiError>({
    queryKey: ['stage-checklist', dealId],
    queryFn: () => api.stageChecklist({ path: { dealId } }),
  });

  if (checklist.isPending) return <Skeleton className="h-block-sm w-full" />;
  // Quiet on failure: this is guidance beside the work, not the work.
  if (checklist.isError) return null;

  const { guidance, requirements } = checklist.data;
  if (!guidance && requirements.length === 0) return null;

  return (
    <Panel data-region="stage-checklist">
      <div className="flex flex-col gap-2">
        <SectionLabel>
          {t('deal.stage.checklist', { values: { stage: checklist.data.stageLabel } })}
        </SectionLabel>

        {guidance ? (
          <p data-stage-guidance className="text-meta text-ink-600">
            {guidance}
          </p>
        ) : null}

        {requirements.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {requirements.map((requirement) => (
              <li
                key={requirement.field}
                data-stage-requirement={requirement.field}
                data-stage-requirement-met={requirement.met ? 'yes' : 'no'}
                className="flex items-center gap-2 text-dense"
              >
                {/* A tick and a word, not a tick alone: a row that says "done"
                    only by being green says nothing on a printed sheet and
                    nothing to somebody who cannot see green. */}
                <Icon
                  name={requirement.met ? 'CheckCircle' : 'Circle'}
                  size="inline"
                  decorative
                />
                <span className={requirement.met ? 'text-ink-meta line-through' : 'text-ink'}>
                  {requirement.field}
                </span>
                <span className="text-meta text-ink-meta">
                  {requirement.met ? t('deal.stage.met') : t('deal.stage.needed')}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}
